import {
  MaintenanceLogWithRelations,
  MaintenanceTaskWithSteps,
  PackageVersionTask,
} from '@markku/types'
import { groupBy, keyBy, sum } from '@markku/utils'

import {
  getCurrentSeasonIndex,
  getMonthRange,
  getSeasonAndYearFromDeadline,
  getSeasonColor,
  getSeasonImage,
  getSeasonName,
  getTaskSeason,
  mapTaskToUI,
  orderTasksByCompletion,
  SeasonData,
  SEASON_ORDER,
} from './utils'

interface SeasonDataResult {
  seasons: SeasonData[]
  logsByTaskId: Map<number, MaintenanceLogWithRelations>
  currentSeasonSlideIndex: number
}

interface BuildSeasonDataParams {
  tasks: MaintenanceTaskWithSteps[]
  logs: MaintenanceLogWithRelations[]
  packageTasks: PackageVersionTask[]
  propertyId: number
  hasPackage: boolean
}

// Pure function for transforming tasks/logs into season data
export const buildSeasonData = ({
  tasks,
  logs,
  packageTasks,
  propertyId,
  hasPackage,
}: BuildSeasonDataParams): SeasonDataResult => {
  // Filter logs with valid maintenanceTaskId for keying
  const logsWithTaskId = logs.filter(
    (log): log is MaintenanceLogWithRelations & { maintenanceTaskId: number } =>
      log.maintenanceTaskId !== null,
  )
  const logsByTaskId = keyBy(logsWithTaskId, (log) => log.maintenanceTaskId)

  const logsBySeason = groupBy(logs, (log) =>
    log.completedDate
      ? getSeasonAndYearFromDeadline(log.completedDate).season
      : 'unknown',
  )

  const tasksBySeason = groupBy(tasks, getTaskSeason)

  const markkuTaskIds = new Set(packageTasks.map((pt) => pt.maintenanceTaskId))

  const currentYear = new Date().getFullYear()
  const completedTaskIds = new Set(
    logsWithTaskId
      .filter(
        (log) =>
          log.completedDate &&
          new Date(log.completedDate).getFullYear() === currentYear,
      )
      .map((log) => log.maintenanceTaskId),
  )

  // Build season data for each season
  const seasonDataMap = new Map(
    SEASON_ORDER.map((season) => {
      const seasonTasks = tasksBySeason.get(season) || []
      const seasonLogs = logsBySeason.get(season) || []

      const totalCost = sum(
        seasonLogs.map((log) =>
          log.actualCost ? parseFloat(log.actualCost) : 0,
        ),
      )

      // Filter tasks into categories
      const ownTasks = seasonTasks.filter((t) => t.propertyId === propertyId)

      const recommendedTasks = seasonTasks.filter(
        (t) =>
          t.propertyId === null &&
          !markkuTaskIds.has(t.id) &&
          t.name !== '__CUSTOM_TASK__',
      )

      const markkuTasks = hasPackage
        ? seasonTasks.filter(
            (t) =>
              t.propertyId === null &&
              markkuTaskIds.has(t.id) &&
              t.name !== '__CUSTOM_TASK__',
          )
        : []

      // Order and transform to UI format
      const uiOwnTasks = orderTasksByCompletion(
        ownTasks,
        completedTaskIds,
        (t) => new Date(t.createdAt),
      ).map((t) => mapTaskToUI(t, completedTaskIds, true))

      const uiRecommendedTasks = orderTasksByCompletion(
        recommendedTasks,
        completedTaskIds,
        (t) => t.name,
      ).map((t) => mapTaskToUI(t, completedTaskIds, true))

      const uiMarkkuTasks = markkuTasks.map((t) =>
        mapTaskToUI(t, completedTaskIds, false),
      )

      const allSeasonTasks = [
        ...uiOwnTasks,
        ...uiRecommendedTasks,
        ...uiMarkkuTasks,
      ]

      return [
        season,
        {
          tasks: uiOwnTasks,
          recommendedTasks: uiRecommendedTasks,
          markkuTasks: uiMarkkuTasks,
          completedCount: allSeasonTasks.filter((t) => t.completed).length,
          totalCount: allSeasonTasks.length,
          totalCost,
        },
      ] as const
    }),
  )

  // Generate 8 seasons: 4 previous + current + 3 future
  const currentSeasonIdx = getCurrentSeasonIndex()
  // In Jan/Feb we're still in the previous year's winter, so adjust base year for season display
  const month = new Date().getMonth()
  const seasonBaseYear = month <= 1 ? currentYear - 1 : currentYear

  const seasons = Array.from({ length: 8 }, (_, i): SeasonData => {
    const offset = i - 4
    const idx = (currentSeasonIdx + offset + 8) % 4
    const season = SEASON_ORDER[idx]
    const data = seasonDataMap.get(season)!
    const year = seasonBaseYear + Math.floor((currentSeasonIdx + offset) / 4)

    // Filter own tasks by year (they have specific deadlines)
    const yearFilteredTasks = data.tasks.filter((task) => {
      if (!task.deadline) return true
      const { year: taskYear } = getSeasonAndYearFromDeadline(task.deadline)
      return taskYear === year
    })

    // Recalculate counts based on filtered tasks
    const allFilteredTasks = [
      ...yearFilteredTasks,
      ...data.recommendedTasks,
      ...data.markkuTasks,
    ]

    return {
      season,
      finnishName: getSeasonName(season),
      year,
      tasks: yearFilteredTasks,
      recommendedTasks: data.recommendedTasks,
      markkuTasks: data.markkuTasks,
      completedCount: allFilteredTasks.filter((t) => t.completed).length,
      totalCount: allFilteredTasks.length,
      monthRange: getMonthRange(season),
      backgroundImage: getSeasonImage(season),
      backgroundColor: getSeasonColor(season),
      totalCost: data.totalCost,
    }
  })

  return {
    seasons,
    logsByTaskId,
    currentSeasonSlideIndex: 4,
  }
}
